import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  FiActivity,
  FiRefreshCw,
  FiMoreHorizontal,
  FiRotateCcw,
  FiTrash2,
  FiEyeOff,
  FiEye,
  FiCheckCircle,
} from 'react-icons/fi';
import AdminLayout from '../components/AdminLayout';
import {
  AdminPageHeader,
  AdminModal,
  AdminToolbar,
  AdminEmpty,
  formatAdminActionLabel,
  adminTimeAgo,
  pillClass,
} from '../components/admin';
import { FormAlert, formInputClass } from '../components/form';
import useAdminPermissions from '../hooks/useAdminPermissions';
import { useApi } from '../hooks/useApi';
import { useThemeClasses } from '../hooks/useThemeClasses';

const PAGE_SIZE = 20;

const AdminActivity = () => {
  const tc = useThemeClasses();
  const { secureFetch } = useApi();
  const { canManageSystem, isSuperAdmin } = useAdminPermissions();
  const canModerate = isSuperAdmin();

  const [scope, setScope] = useState('platform');
  const [includeHidden, setIncludeHidden] = useState(false);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [acting, setActing] = useState(false);

  const [manageOpen, setManageOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => window.clearTimeout(t);
  }, [search]);

  const fetchActivities = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const offset = (page - 1) * PAGE_SIZE;
      const params = new URLSearchParams({
        scope,
        limit: String(PAGE_SIZE),
        offset: String(offset),
        q: debouncedSearch,
      });
      if (includeHidden) params.set('includeHidden', '1');
      const res = await secureFetch(`/api/admin/activities?${params}`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'No se pudo cargar la actividad');
      setItems(data.items || []);
      setTotal(data.total ?? 0);
    } catch (err) {
      setError(err.message || 'Error al cargar actividades.');
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [secureFetch, scope, page, debouncedSearch, includeHidden]);

  useEffect(() => {
    setPage(1);
  }, [scope, debouncedSearch, includeHidden]);

  useEffect(() => {
    fetchActivities();
  }, [fetchActivities]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const openManage = (row) => {
    setSelectedRow(row);
    setConfirmDelete(false);
    setManageOpen(true);
  };

  const closeManage = () => {
    setManageOpen(false);
    setSelectedRow(null);
    setConfirmDelete(false);
  };

  const runAction = async (type) => {
    if (!selectedRow?.id || !canModerate) return;
    setActing(true);
    setError('');
    try {
      const res = await secureFetch(`/api/admin/activities/${selectedRow.id}/actions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ type }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'No se pudo aplicar la acción');

      if (data.deleted) {
        setItems((prev) => prev.filter((row) => row.id !== selectedRow.id));
        setTotal((t) => Math.max(0, t - 1));
        closeManage();
        return;
      }

      if (data.item) {
        setItems((prev) => prev.map((row) => (row.id === data.item.id ? data.item : row)));
        setSelectedRow(data.item);
        if (type === 'hide' && !includeHidden) {
          setItems((prev) => prev.filter((row) => row.id !== data.item.id));
          setTotal((t) => Math.max(0, t - 1));
          closeManage();
        }
      }
      if (type !== 'delete') setConfirmDelete(false);
    } catch (err) {
      setError(err.message || 'Error al aplicar la acción.');
    } finally {
      setActing(false);
    }
  };

  const scopeFilters = useMemo(
    () => (
      <div className="flex flex-col sm:flex-row gap-2">
        <select
          value={scope}
          onChange={(e) => setScope(e.target.value)}
          className={formInputClass(tc.inputClass, { compact: true })}
        >
          <option value="platform">Plataforma</option>
          <option value="all">Toda la actividad</option>
        </select>
        {canModerate && (
          <label className={`flex items-center gap-2 text-xs px-2 ${tc.textSec}`}>
            <input
              type="checkbox"
              checked={includeHidden}
              onChange={(e) => setIncludeHidden(e.target.checked)}
              className="rounded border-gray-400"
            />
            Incluir ocultas
          </label>
        )}
      </div>
    ),
    [scope, includeHidden, canModerate, tc.inputClass, tc.textSec]
  );

  const actionBtn = (label, type, icon, variant = 'outline', disabled = false) => (
    <button
      key={type}
      type="button"
      disabled={acting || disabled}
      onClick={() => {
        if (type === 'delete') {
          setConfirmDelete(true);
          return;
        }
        runAction(type);
      }}
      className={`inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-xs font-semibold border disabled:opacity-50 ${
        variant === 'danger'
          ? tc.modoOscuro
            ? 'border-red-800 text-red-300 hover:bg-red-950/40'
            : 'border-red-200 text-red-700 hover:bg-red-50'
          : tc.btnOutline
      }`}
    >
      {icon}
      {label}
    </button>
  );

  return (
    <AdminLayout requiredPermission={canManageSystem}>
      <div className="max-w-5xl mx-auto py-4 sm:py-6 space-y-4">
        <AdminPageHeader
          title="Actividad reciente"
          subtitle="Auditoría de la plataforma. Gestioná registros con acciones (revertir, ocultar, eliminar)."
          actions={
            <button
              type="button"
              onClick={fetchActivities}
              className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border text-xs ${tc.btnOutline}`}
            >
              <FiRefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
              Actualizar
            </button>
          }
        />

        {error && <FormAlert type="error">{error}</FormAlert>}

        {!canModerate && (
          <FormAlert type="info" className="text-sm">
            Solo el super administrador puede aplicar acciones sobre registros.
          </FormAlert>
        )}

        <AdminToolbar
          searchValue={search}
          onSearchChange={setSearch}
          searchPlaceholder="Buscar por acción o detalle…"
          filters={scopeFilters}
          meta={`${total} registro${total === 1 ? '' : 's'} · ${scope === 'platform' ? 'plataforma' : 'todas las acciones'}`}
        />

        <section className={`rounded-2xl border ${tc.box}`}>
          {loading && !items.length ? (
            <p className={`p-6 text-sm ${tc.textMuted}`}>Cargando actividad…</p>
          ) : !items.length ? (
            <div className="p-6">
              <AdminEmpty
                icon={FiActivity}
                title="Sin resultados"
                subtitle="Probá otro filtro o ampliá a «Toda la actividad»."
              />
            </div>
          ) : (
            <ul className={`divide-y ${tc.modoOscuro ? 'divide-gray-800' : 'divide-gray-100'}`}>
              {items.map((row) => (
                <li key={row.id} className="p-4 flex items-start gap-3">
                  <div
                    className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${
                      tc.modoOscuro ? 'bg-gray-800 text-gray-300' : 'bg-gray-100 text-gray-700'
                    }`}
                  >
                    <FiActivity className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className={`text-sm font-semibold ${tc.textPri}`}>
                        {formatAdminActionLabel(row.action)}
                      </p>
                      {row.adminReviewed && (
                        <span className={pillClass(tc.modoOscuro, 'success')}>Revisada</span>
                      )}
                      {row.adminHidden && (
                        <span className={pillClass(tc.modoOscuro, 'neutral')}>Oculta</span>
                      )}
                    </div>
                    {row.details ? (
                      <p className={`text-xs mt-1 whitespace-pre-wrap break-words ${tc.textSec}`}>
                        {row.details}
                      </p>
                    ) : null}
                    <p className={`text-[11px] mt-2 ${tc.textMuted}`}>
                      {row.user || 'Sistema'}
                      {row.userId ? ` · ID ${row.userId}` : ''}
                      {' · '}
                      {adminTimeAgo(row.createdAt)}
                      <span className="hidden sm:inline">
                        {' '}
                        · {new Date(row.createdAt).toLocaleString('es-AR')}
                      </span>
                    </p>
                  </div>
                  {canModerate && (
                    <button
                      type="button"
                      onClick={() => openManage(row)}
                      className={`shrink-0 px-2.5 py-2 rounded-lg border text-xs font-medium inline-flex items-center gap-1.5 ${tc.btnOutline}`}
                    >
                      <FiMoreHorizontal className="w-4 h-4" />
                      Gestionar
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}

          {total > PAGE_SIZE && (
            <div className={`flex items-center justify-between gap-3 px-4 py-3 border-t text-xs ${tc.divide}`}>
              <span className={tc.textMuted}>
                Página {page} de {totalPages}
              </span>
              <div className="flex gap-2">
                <button
                  type="button"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  className={`px-3 py-1.5 rounded-lg border disabled:opacity-40 ${tc.btnOutline}`}
                >
                  Anterior
                </button>
                <button
                  type="button"
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  className={`px-3 py-1.5 rounded-lg border disabled:opacity-40 ${tc.btnOutline}`}
                >
                  Siguiente
                </button>
              </div>
            </div>
          )}
        </section>

        <p className={`text-center text-xs ${tc.textMuted}`}>
          <Link to="/admin/dashboard" className="text-[#1F80FF] hover:underline">
            Volver al panel
          </Link>
          {' · '}
          <Link to="/admin/system" className="text-[#1F80FF] hover:underline">
            Estado del sistema
          </Link>
        </p>
      </div>

      <AdminModal
        open={manageOpen}
        onClose={closeManage}
        title="Gestionar registro"
        subtitle="Elegí una acción. No se edita texto a mano."
        wide
        footer={
          <button type="button" onClick={closeManage} className={`px-4 py-2 rounded-lg border text-sm ${tc.btnOutline}`}>
            Cerrar
          </button>
        }
      >
        {selectedRow && (
          <div className="space-y-4">
            <div className={`rounded-xl border p-3 text-sm ${tc.section}`}>
              <p className={`font-semibold ${tc.textPri}`}>{formatAdminActionLabel(selectedRow.action)}</p>
              {selectedRow.details ? (
                <p className={`text-xs mt-2 whitespace-pre-wrap break-words ${tc.textSec}`}>{selectedRow.details}</p>
              ) : null}
              <p className={`text-[11px] mt-2 ${tc.textMuted}`}>
                {selectedRow.user || 'Sistema'} · {new Date(selectedRow.createdAt).toLocaleString('es-AR')}
              </p>
            </div>

            {confirmDelete ? (
              <div className={`rounded-xl border p-3 ${tc.modoOscuro ? 'border-red-900 bg-red-950/30' : 'border-red-200 bg-red-50'}`}>
                <p className={`text-sm font-medium ${tc.textPri}`}>¿Eliminar este registro?</p>
                <p className={`text-xs mt-1 ${tc.textSec}`}>
                  Se borra de la auditoría y queda constancia en el log de administración.
                </p>
                <div className="mt-3 flex flex-wrap gap-2">
                  <button
                    type="button"
                    disabled={acting}
                    onClick={() => runAction('delete')}
                    className={`px-3 py-2 rounded-lg text-xs font-semibold text-white bg-red-600 hover:bg-red-700 disabled:opacity-50`}
                  >
                    Sí, eliminar
                  </button>
                  <button
                    type="button"
                    onClick={() => setConfirmDelete(false)}
                    className={`px-3 py-2 rounded-lg text-xs border ${tc.btnOutline}`}
                  >
                    Cancelar
                  </button>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {actionBtn(
                  selectedRow.revertLabel || 'Revertir',
                  'revert',
                  <FiRotateCcw className="w-3.5 h-3.5" />,
                  'outline',
                  !selectedRow.canRevert || selectedRow.adminUndone
                )}
                {selectedRow.adminHidden
                  ? actionBtn('Mostrar de nuevo', 'restore', <FiEye className="w-3.5 h-3.5" />)
                  : actionBtn('Ocultar del listado', 'hide', <FiEyeOff className="w-3.5 h-3.5" />)}
                {selectedRow.adminReviewed
                  ? actionBtn('Quitar «revisada»', 'clear_reviewed', <FiCheckCircle className="w-3.5 h-3.5" />)
                  : actionBtn('Marcar como revisada', 'mark_reviewed', <FiCheckCircle className="w-3.5 h-3.5" />)}
                {actionBtn('Eliminar registro', 'delete', <FiTrash2 className="w-3.5 h-3.5" />, 'danger')}
              </div>
            )}

            <p className={`text-[11px] ${tc.textMuted}`}>
              {selectedRow.revertHint
                || (selectedRow.canRevert
                  ? 'Aplicá la acción de reversión según el tipo de evento.'
                  : 'Este registro no admite reversión (o ya fue revertido).')}
            </p>
          </div>
        )}
      </AdminModal>
    </AdminLayout>
  );
};

export default AdminActivity;
